import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal } from '@angular/core';
import { MatRadioButton, MatRadioChange, MatRadioGroup } from '@angular/material/radio';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { Store } from '@ngrx/store';
import { AiSettingsActions } from '../../state/review-actions';
import { selectAiClosings, selectAiDefaultLanguages, selectAiGreetings, selectAiLanguageForReviewType, selectAiReplyStyle, selectDefaultAiLanguageSelected, selectSelectedAiClosing, selectSelectedAiGreeting } from '../../state/review.selectors';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EditGreetingsComponent } from './edit-greetings/edit-greetings.component';
import { MatTooltip } from '@angular/material/tooltip';
import { ReplyTemplateService } from '../../reply-template.service';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { cleanStringForComparison } from '../../../../core/utilities';
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { HighlightKeywordPipe } from '../../../../pipes/highlight-keyword.pipe';
import { AiRepliesLanguage } from '../../state/review.state';
import { MatDivider } from '@angular/material/divider';
import { GxInputDirective } from '../../../../directives/gxinput.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface AiSettings {
  id: number;
  userProfileId: number;
  title: string;
  type: AiSettingsType;
  reviewType: AiSettingsReviewType;
  text: string;
  createTime: Date;
  editTime: Date;
  selected: boolean;
}

export enum AiSettingsReviewType {
  ALL = 'ALL',
  STAR_ONLY = 'STAR_ONLY'
}

export enum ReplyStyle {
  STANDARD = 'STANDARD',
  FORMAL = 'FORMAL',
  INFORMAL = 'INFORMAL'
}

export interface AiSettingsCreatePayload {
  type: AiSettingsType,
  title: string,
  text: string,
  selected: boolean
}

export interface AiSettingsUpdatePayload {
  title: string,
  text: string,
  selected: boolean
}

export enum AiSettingsType {
  GREETING = 'GREETING',
  CLOSING = 'CLOSING',
  LANGUAGE = 'LANGUAGE',
  STYLE = 'STYLE'
}

@Component({
  selector: 'app-ai-replies-settings',
  standalone: true,
  imports: [
    MatRadioGroup,
    FormsModule,
    MatRadioButton,
    MatIcon,
    MatIconButton,
    MatFormField,
    MatButton,
    MatInput,
    ReactiveFormsModule,
    TranslateModule,
    EditGreetingsComponent,
    MatTooltip,
    MatPrefix,
    MatSuffix,
    MatOption,
    MatSelect,
    MatButtonToggleGroup,
    MatButtonToggle,
    HighlightKeywordPipe,
    MatSelectTrigger,
    MatDivider,
    GxInputDirective,
  ],
  templateUrl: './ai-replies-settings.component.html',
  styleUrl: './ai-replies-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiRepliesSettingsComponent {
  private readonly store = inject(Store);
  private readonly replyTemplateService = inject(ReplyTemplateService);
  private readonly translate = inject(TranslateService);
  private readonly cd = inject(ChangeDetectorRef);

  // Local Signal State Variables
  protected readonly editingOpen = signal(false);
  protected readonly editingAiSettings = signal<AiSettings | null>(null);
  protected readonly editingMode = signal(false);
  protected readonly aiSettingType = signal<AiSettingsType>(AiSettingsType.GREETING);

  // default language search box
  searchFieldFormControl = new FormControl('', { nonNullable: true });

  // Separate, granular global Signals
  protected readonly greetings = this.store.selectSignal(selectAiGreetings);
  protected readonly closings = this.store.selectSignal(selectAiClosings);
  protected readonly selectedGreeting = this.store.selectSignal(selectSelectedAiGreeting);
  protected readonly selectedClosing = this.store.selectSignal(selectSelectedAiClosing);
  protected readonly defaultLanguage = this.store.selectSignal(selectDefaultAiLanguageSelected);
  protected readonly defaultLanguages = this.store.selectSignal(selectAiDefaultLanguages);
  protected readonly defaultLanguageReviewType = this.store.selectSignal(selectAiLanguageForReviewType);
  protected readonly replyStyle = this.store.selectSignal(selectAiReplyStyle);

  protected readonly ReplyStyle = ReplyStyle;
  protected readonly AiSettingsType = AiSettingsType;
  protected readonly ReviewType = AiSettingsReviewType;

  constructor() {
    this.store.dispatch(AiSettingsActions.getDefaultLanguages());
    this.store.dispatch(AiSettingsActions.getAiSettings());

    // run CD detection on language change to update dropdown language display names
    this.translate.onLangChange
        .pipe(takeUntilDestroyed())
        .subscribe(() => this.cd.markForCheck());
  }

  onGreetingSelected(event: MatRadioChange, selectedGreeting: AiSettings | null | undefined) {
    // Always dispatch unselect if a greeting was selected
    if (selectedGreeting) {
      this.store.dispatch(AiSettingsActions.unSelectAiSettings({ aiSettings: selectedGreeting }));
    }

    // If a new greeting is selected, dispatch select
    if (event.value) {
      this.store.dispatch(AiSettingsActions.selectAiSettings({ aiSettings: event.value }));
    }
  }

  onClosingSelected(event: MatRadioChange, selectedClosing: AiSettings | null | undefined) {
    // Always dispatch unselect if a previous greeting was selected
    if (selectedClosing) {
      this.store.dispatch(AiSettingsActions.unSelectAiSettings({ aiSettings: selectedClosing }));
    }

    // If a new greeting is selected, dispatch select
    if (event.value) {
      this.store.dispatch(AiSettingsActions.selectAiSettings({ aiSettings: event.value }));
    }
  }

  onOpenEdit(greeting: AiSettings) {
    this.editingAiSettings.set(greeting);
    this.aiSettingType.set(greeting.type);
    this.editingMode.set(true);
    this.editingOpen.set(true);
  }

  onSave($event: AiSettings) {
    this.editingOpen.set(!this.editingOpen());
    this.store.dispatch(AiSettingsActions.saveAiSettings({ aiSettings: $event }));
  }

  onDelete($event: number) {
    this.editingOpen.set(!this.editingOpen());
    this.store.dispatch(AiSettingsActions.deleteAiSettings({ id: $event }));
  }

  onBack() {
    this.editingOpen.set(!this.editingOpen());
  }

  createNew(type: AiSettingsType) {
    this.editingAiSettings.set(null);
    this.aiSettingType.set(type);
    this.editingOpen.set(!this.editingOpen());
    this.editingMode.set(false);
  }

  /**
   * Wraps all matched tags into a <span class="mention"> element, which will allow highlighting
   * them with CSS
   * @param templateText
   */
  protected generateTemplateInnerHTML(templateText: string): string {
    return this.replyTemplateService.generateTemplateInnerHTML(templateText);
  }

  /**
   * Update default language for Ai-generated review replies
   * @param event
   * @param defaultLanguageReviewType
   * @param defaultLanguage
   */
  updateDefaultLanguageAndReviewType(event: Object | MatButtonToggleChange,
                                     defaultLanguageReviewType: AiSettingsReviewType | null | undefined,
                                     defaultLanguage: AiRepliesLanguage | null | undefined) {
    let newLanguage: string | undefined;
    let newReviewType: AiSettingsReviewType = defaultLanguageReviewType || AiSettingsReviewType.ALL; // Keep existing by default

    if (event instanceof MatButtonToggleChange) {
      newLanguage = defaultLanguage?.key; // Keep existing language
      newReviewType = event.value;
    } else {
      newLanguage = (event as AiRepliesLanguage).key;
    }

    this.store.dispatch(AiSettingsActions.updateDefaultLanguage({
      defaultLanguage: newLanguage,
      reviewType: newReviewType,
    }));
  }

  onStyleSelected(event: MatButtonToggleChange) {
    this.store.dispatch(AiSettingsActions.updateReplyStyle({ style: event.value }));
  }

  /**
   * Get language display name for the dropdown to show depending on selected app language
   * @param selectedDefaultLanguage
   */
  getLanguageDisplayName(selectedDefaultLanguage: AiRepliesLanguage | null | undefined) {
    if (!selectedDefaultLanguage) {
      return '';
    }
    return this.translate.currentLang === 'en' ? selectedDefaultLanguage?.enName :
        selectedDefaultLanguage?.deName;
  }

  /**
   * Get emoji
   * @param selectedDefaultLanguage
   */
  getEmoji(selectedDefaultLanguage: AiRepliesLanguage | null | undefined) {
    if (!selectedDefaultLanguage) {
      return '';
    }

    const codePoints = selectedDefaultLanguage.emojiCode.split(' ').map(code => parseInt(code.replace('U+', ''), 16));
    return String.fromCodePoint(...codePoints);
  }

  /**
   * Will hide default language options (depending on selected app language) that do not match the
   * search string
   * @param defaultLanguage
   */
  isHidden(defaultLanguage: AiRepliesLanguage): boolean {
    if (!this.searchFieldFormControl?.value?.length) {
      return false;
    }
    // if search is active: hide all language names that don't contain the searchKeyword
    const cleanedLanguageName = cleanStringForComparison(this.translate.currentLang === 'en' ?
        defaultLanguage.enName : defaultLanguage.deName);
    const cleanedSearchString = cleanStringForComparison(this.searchFieldFormControl.value);
    return !cleanedLanguageName.includes(cleanedSearchString);
  }
}
